import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useCapaAccess } from "./capaAccess";
import { fetchCapaConfig, saveCapaConfig } from "./capaService";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import CapaActionPlanningEmailConfigEditor from "./CapaActionPlanningEmailConfigEditor";
import { CAPA_ACTION_PLANNING_DECISION_EMAIL_MERGE_TAGS } from "./capaActionPlanningEmailTags";
import {
  CAPA_IMPLEMENTATION_ASSIGNMENT_EMAIL_MERGE_TAGS,
  CAPA_IMPLEMENTATION_OWNER_DECISION_EMAIL_MERGE_TAGS,
} from "./capaImplementationEmailTags";
import {
  CAPA_APPROVAL_ASSIGNMENT_EMAIL_MERGE_TAGS,
  CAPA_APPROVAL_DECISION_EMAIL_MERGE_TAGS,
} from "./capaApprovalEmailTags";
import {
  CAPA_VERIFICATION_ASSIGNMENT_EMAIL_MERGE_TAGS,
  CAPA_VERIFICATION_OWNER_OUTCOME_EMAIL_MERGE_TAGS,
} from "./capaVerificationEmailTags";
import "./CapaPages.scss";

const CapaConfig = () => {
  const navigate = useNavigate();
  const { userData, canCapaConfigure } = useCapaAccess();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = await fetchCapaConfig(userData);
      if (!cancelled) setCfg(c);
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const handleSave = async () => {
    if (!cfg) return;
    await saveCapaConfig(cfg, userData);
    showAlert({
      title: "Config",
      message: "CAPA configuration saved.",
      type: "success",
    });
  };

  const patchCfg = useCallback((partial) => {
    setCfg((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canCapaConfigure) return <Navigate to="/notification-center" replace />;

  return (
    <div className="capa-page capa-page-cap-config">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "CAPA configuration" },
        ]}
        actions={[
          { icon: "fas fa-list", label: "CAPA list", onClick: () => navigate("/capa") },
        ]}
      />

      <div className="card capa-cap-config-fill-card shadow-sm">
        <div className="card-body capa-cap-config-card-body d-flex flex-column min-vh-0">
          {cfg ? (
            <>
              <div className="flex-shrink-0">
                <div className="mb-3">
                  <label className="form-label">Numbering format</label>
                  <input className="form-control" readOnly value={cfg.numberingFormat} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Default SLA (days)</label>
                  <input
                    className="form-control"
                    readOnly
                    value={cfg.defaultSlaDays}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Statuses (static)</label>
                  <input
                    className="form-control"
                    readOnly
                    value={(cfg.statuses || []).join(", ")}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Source types</label>
                  <input
                    className="form-control"
                    readOnly
                    value={(cfg.sourceTypes || []).join(", ")}
                  />
                </div>
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Action planning email template</h5>
              <div className="capa-ap-email-editor-grow flex-grow-1 min-vh-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.actionPlanningEmailSubjectTemplate}
                  onSubjectChange={(v) =>
                    patchCfg({ actionPlanningEmailSubjectTemplate: v })
                  }
                  bodyTemplate={cfg.actionPlanningEmailBodyTemplate}
                  onBodyChange={(html) =>
                    patchCfg({ actionPlanningEmailBodyTemplate: html })
                  }
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description={
                    "Default subject and HTML body are defined in the database (CAPAConfig.ActionPlanningEmailSubjectDefault / ActionPlanningEmailBodyDefault). The fields below show your effective templates when your organization has not saved overrides yet. Merge tags must match the entity names in the table (including casing)."
                  }
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Action planning — task accepted (notify CAPA owner)</h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.actionPlanningTaskAcceptEmailSubjectTemplate}
                  onSubjectChange={(v) =>
                    patchCfg({ actionPlanningTaskAcceptEmailSubjectTemplate: v })
                  }
                  bodyTemplate={cfg.actionPlanningTaskAcceptEmailBodyTemplate}
                  onBodyChange={(html) =>
                    patchCfg({ actionPlanningTaskAcceptEmailBodyTemplate: html })
                  }
                  mergeTags={CAPA_ACTION_PLANNING_DECISION_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Emailed to the CAPA owner when an assignee accepts a task (CAPAConfig ActionPlanningTaskAccept*). Same layout as assignment: subject, body, and merge tags on the right. Click a tag to copy it."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Action planning — task rejected (notify CAPA owner)</h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.actionPlanningTaskRejectEmailSubjectTemplate}
                  onSubjectChange={(v) =>
                    patchCfg({ actionPlanningTaskRejectEmailSubjectTemplate: v })
                  }
                  bodyTemplate={cfg.actionPlanningTaskRejectEmailBodyTemplate}
                  onBodyChange={(html) =>
                    patchCfg({ actionPlanningTaskRejectEmailBodyTemplate: html })
                  }
                  mergeTags={CAPA_ACTION_PLANNING_DECISION_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Emailed to the CAPA owner when an assignee rejects a task (CAPAConfig ActionPlanningTaskReject*). Same merge tags as the accepted template. Click a tag to copy it."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Implementation — task assignment (notify implementer)</h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.implAssignmentEmailSubjectTemplate}
                  onSubjectChange={(v) => patchCfg({ implAssignmentEmailSubjectTemplate: v })}
                  bodyTemplate={cfg.implAssignmentEmailBodyTemplate}
                  onBodyChange={(html) => patchCfg({ implAssignmentEmailBodyTemplate: html })}
                  mergeTags={CAPA_IMPLEMENTATION_ASSIGNMENT_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Sent when an implementation task is created and assigned. Defaults: CAPAConfig ImplAssignmentEmail*Default. Merge tags match the assignment payload."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Implementation — status change (notify CAPA owner)</h5>
              <p className="text-muted small mb-3 flex-shrink-0">
                One template for every implementation status change (including Deferred). Merge tokens{" "}
                <strong>PreviousStatus</strong> and <strong>NewStatus</strong> show the old and new values;{" "}
                <strong>Decision</strong> repeats the new status. Stored as ImplOwnerAccept* in CAPAConfig;
                legacy ImplOwnerReject* columns are kept in sync when you save.
              </p>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.implOwnerAcceptEmailSubjectTemplate}
                  onSubjectChange={(v) => patchCfg({ implOwnerAcceptEmailSubjectTemplate: v })}
                  bodyTemplate={cfg.implOwnerAcceptEmailBodyTemplate}
                  onBodyChange={(html) => patchCfg({ implOwnerAcceptEmailBodyTemplate: html })}
                  mergeTags={CAPA_IMPLEMENTATION_OWNER_DECISION_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Use {{PreviousStatus}}, {{NewStatus}}, {{DecisionReason}}, {{DecidedByName}}, {{TaskAssigneeName}}, and {{DueDate}}. Defaults: ImplOwnerAcceptEmail*."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Verification — assign request (notify verifier)</h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.verificationAssignmentEmailSubjectTemplate}
                  onSubjectChange={(v) =>
                    patchCfg({ verificationAssignmentEmailSubjectTemplate: v })
                  }
                  bodyTemplate={cfg.verificationAssignmentEmailBodyTemplate}
                  onBodyChange={(html) =>
                    patchCfg({ verificationAssignmentEmailBodyTemplate: html })
                  }
                  mergeTags={CAPA_VERIFICATION_ASSIGNMENT_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Sent when a verification request is created in the Verification stage. Defaults: CAPAConfig VerificationAssignmentEmail*Default."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">
                Verification — outcome Effective (notify CAPA owner)
              </h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.verificationOutcomeEffectiveEmailSubjectTemplate}
                  onSubjectChange={(v) =>
                    patchCfg({ verificationOutcomeEffectiveEmailSubjectTemplate: v })
                  }
                  bodyTemplate={cfg.verificationOutcomeEffectiveEmailBodyTemplate}
                  onBodyChange={(html) =>
                    patchCfg({ verificationOutcomeEffectiveEmailBodyTemplate: html })
                  }
                  mergeTags={CAPA_VERIFICATION_OWNER_OUTCOME_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Emailed to the CAPA owner when the verifier submits an effective outcome. VerificationOutcomeEffectiveEmail*."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">
                Verification — outcome Not Effective (notify CAPA owner)
              </h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.verificationOutcomeNotEffectiveEmailSubjectTemplate}
                  onSubjectChange={(v) =>
                    patchCfg({ verificationOutcomeNotEffectiveEmailSubjectTemplate: v })
                  }
                  bodyTemplate={cfg.verificationOutcomeNotEffectiveEmailBodyTemplate}
                  onBodyChange={(html) =>
                    patchCfg({ verificationOutcomeNotEffectiveEmailBodyTemplate: html })
                  }
                  mergeTags={CAPA_VERIFICATION_OWNER_OUTCOME_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Emailed to the CAPA owner when the verifier submits a not effective outcome. VerificationOutcomeNotEffectiveEmail*."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Approval — assignment (notify approver)</h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.approvalAssignmentEmailSubjectTemplate}
                  onSubjectChange={(v) => patchCfg({ approvalAssignmentEmailSubjectTemplate: v })}
                  bodyTemplate={cfg.approvalAssignmentEmailBodyTemplate}
                  onBodyChange={(html) => patchCfg({ approvalAssignmentEmailBodyTemplate: html })}
                  mergeTags={CAPA_APPROVAL_ASSIGNMENT_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Emailed when someone is assigned as a CAPA approver (CAPAConfig ApprovalAssignment*). Defaults are stored in the database; fields show effective templates. Click a tag to copy it."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Approval — accepted (notify CAPA owner)</h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.approvalDecisionAcceptEmailSubjectTemplate}
                  onSubjectChange={(v) => patchCfg({ approvalDecisionAcceptEmailSubjectTemplate: v })}
                  bodyTemplate={cfg.approvalDecisionAcceptEmailBodyTemplate}
                  onBodyChange={(html) => patchCfg({ approvalDecisionAcceptEmailBodyTemplate: html })}
                  mergeTags={CAPA_APPROVAL_DECISION_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Emailed to the CAPA owner when an approver accepts (CAPAConfig ApprovalDecisionAccept*). Click a tag to copy it."
                  fillAvailableHeight
                />
              </div>

              <hr className="my-3 flex-shrink-0" />
              <h5 className="mb-2 flex-shrink-0">Approval — rejected (notify CAPA owner)</h5>
              <div className="capa-ap-email-decision-section mb-3 flex-shrink-0">
                <CapaActionPlanningEmailConfigEditor
                  subjectTemplate={cfg.approvalDecisionRejectEmailSubjectTemplate}
                  onSubjectChange={(v) => patchCfg({ approvalDecisionRejectEmailSubjectTemplate: v })}
                  bodyTemplate={cfg.approvalDecisionRejectEmailBodyTemplate}
                  onBodyChange={(html) => patchCfg({ approvalDecisionRejectEmailBodyTemplate: html })}
                  mergeTags={CAPA_APPROVAL_DECISION_EMAIL_MERGE_TAGS}
                  onTagCopied={(tag) =>
                    showAlert({
                      title: "Copied",
                      message: `${tag} copied to clipboard.`,
                      type: "success",
                    })
                  }
                  description="Emailed to the CAPA owner when an approver rejects (CAPAConfig ApprovalDecisionReject*). Same merge tags as accept. Click a tag to copy it."
                  fillAvailableHeight
                />
              </div>
            </>
          ) : (
            <div className="text-muted py-3 flex-shrink-0">Loading configuration…</div>
          )}
          <div className="capa-ap-email-panel-footer d-flex justify-content-end">
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!cfg}>
              Save
            </button>
          </div>
        </div>
      </div>

      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
    </div>
  );
};

export default CapaConfig;
